// Same localStorage key and value shape the zion flag overlay (?showFlags) writes;
// @fs/flags-js checks these overrides before consulting Split.
const STORAGE_KEY = 'fsZionFlags'

// The opt-in lives under its own key, not fsZionFlags: zion-root shows its internal
// flag-overrides button whenever fsZionFlags holds anything, and participants must
// never see internal tooling (STD-19234)
export const PRE_RELEASE_OPT_IN_KEY = 'placesPreRelease'

// Umbrella flag the feedback group's standing opt-in link enables
// (?flags=standards_places_preRelease:on — see docs/feature-flags.md).
export const PRE_RELEASE_FLAG = 'standards_places_preRelease'

type FlagOverrides = Record<string, { treatment: string; config: null }>

function readOverrides(): FlagOverrides {
  try {
    const parsed = JSON.parse(window.localStorage.getItem(STORAGE_KEY) || '{}')
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {}
  } catch {
    return {}
  }
}

export function hasFlagOverrides(): boolean {
  return Object.keys(readOverrides()).length > 0
}

export function clearFlagOverrides(): void {
  window.localStorage.removeItem(STORAGE_KEY)
  window.localStorage.removeItem(PRE_RELEASE_OPT_IN_KEY)
}

export function hasPreReleaseOptIn(): boolean {
  try {
    return window.localStorage.getItem(PRE_RELEASE_OPT_IN_KEY) === 'on'
  } catch {
    return false
  }
}

// 'off' only clears the stored opt-in; it can't override a Split-targeted one (none exist today)
function setPreReleaseOptIn(treatment: string): void {
  if (treatment === 'on') window.localStorage.setItem(PRE_RELEASE_OPT_IN_KEY, 'on')
  else window.localStorage.removeItem(PRE_RELEASE_OPT_IN_KEY)
}

// Opt-ins made before STD-19234 sit in fsZionFlags; move them to their own key
function migrateLegacyOptIn(): void {
  const overrides = readOverrides()
  const legacy = overrides[PRE_RELEASE_FLAG]
  if (!legacy) return
  setPreReleaseOptIn(legacy.treatment)
  delete overrides[PRE_RELEASE_FLAG]
  if (Object.keys(overrides).length > 0) window.localStorage.setItem(STORAGE_KEY, JSON.stringify(overrides))
  else window.localStorage.removeItem(STORAGE_KEY)
}

// Applies the feedback group's one-click opt-in links:
//   ?flags=standards_places_preRelease:on   ?flags=clear
// Only the pre-release flag can be set from a URL — it is kill-switched per
// environment in Split (see usePreReleaseFlag), unlike arbitrary flags, which
// would win their override unconditionally and stay overlay-only on purpose.
// Must run before React renders so this page load sees the opt-in (no reload
// needed). Strips the param afterward so copied URLs don't re-apply it.
export default function applyFlagLinkOverrides(): void {
  // Runs before render, so a storage failure must not throw; the user just stays on the standard experience
  try {
    migrateLegacyOptIn()
  } catch {
    // storage unavailable
  }

  const params = new URLSearchParams(window.location.search)
  const flagsParam = params.get('flags')
  if (!flagsParam) return

  const [name, treatment] = flagsParam.split(':')
  const isOptInLink = name === PRE_RELEASE_FLAG && (treatment === 'on' || treatment === 'off')
  if (flagsParam !== 'clear' && !isOptInLink) return

  try {
    if (flagsParam === 'clear') clearFlagOverrides()
    else setPreReleaseOptIn(treatment)
  } catch {
    // storage unavailable
  }

  params.delete('flags')
  const query = params.toString()
  const search = query ? `?${query}` : ''
  window.history.replaceState(null, '', `${window.location.pathname}${search}${window.location.hash}`)
}
